import React, { useState, useEffect } from "react";
import { useThemeClasses } from "../lib/themes/utils";
import { Navigation } from "./common/Navigation";
import { Footer } from "./common/Footer";

interface ContentItem {
  id: number;
  title: string;
  description: string;
  price_cents: number;
  currency: string;
  tags?: string;
  content_type: string;
  sort_order: number;
  created_at: string;
}

export const ContentCatalog: React.FC = () => {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<number | string | null>(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState<
    number | string | null
  >(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [priceFilter, setPriceFilter] = useState<
    "all" | "under50" | "50to100" | "over100"
  >("all");
  const [savedItems, setSavedItems] = useState<number[]>([]);

  const theme = useThemeClasses();

  useEffect(() => {
    fetchContentCatalog();
  }, []);

  const fetchContentCatalog = async () => {
    try {
      const response = await fetch("/api/content-catalog");

      if (!response.ok) {
        throw new Error("Failed to fetch content catalog");
      }

      const data = await response.json();
      setContentItems(data.success ? data.data.content_items : []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load content catalog"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleContentPurchase = async (item: ContentItem) => {
    if (!customerEmail) {
      setError("Please enter your email address");
      return;
    }

    setPurchasing(item.id);
    setError(null);

    try {
      const response = await fetch("/api/public-purchase-content", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contentId: item.id,
          customerEmail,
          customerName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.should_login) {
          setError(`${data.error} Click here to log in instead.`);
        } else if (data.pending_purchase) {
          setError(data.error);
        } else if (data.recent_purchase) {
          setError(
            `${data.error} If you completed payment, check your email for access instructions.`
          );
        } else {
          throw new Error(data.error || "Failed to initiate purchase");
        }
        return;
      }

      if (data.success && data.data.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case "pdf":
        return "📋";
      case "video":
        return "🎬";
      case "audio":
        return "🎧";
      case "guide":
        return "🌱";
      case "recipe":
        return "🥗";
      case "meditation":
        return "🧘‍♀️";
      case "group_session":
        return "👥";
      case "intake_form":
        return "🌟";
      default:
        return "🌿";
    }
  };

  const filteredItems = contentItems.filter((item) => {
    // Type filter
    if (activeFilter !== "all" && item.content_type !== activeFilter)
      return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        (item.tags &&
          JSON.parse(item.tags).some((tag: string) =>
            tag.toLowerCase().includes(query)
          ));
      if (!matchesSearch) return false;
    }

    // Price filter
    if (priceFilter !== "all") {
      const price = item.price_cents / 100;
      if (priceFilter === "under50" && price >= 50) return false;
      if (priceFilter === "50to100" && (price < 50 || price > 100))
        return false;
      if (priceFilter === "over100" && price <= 100) return false;
    }

    return true;
  });

  const contentTypes = Array.from(
    new Set(contentItems.map((item) => item.content_type))
  );

  const toggleSaveItem = (itemId: number) => {
    setSavedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${theme.backgrounds.gradient} flex justify-center items-center`}
      >
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">🌿</div>
          <div className={`text-xl ${theme.typography.heading}`}>
            Gathering wellness resources...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient}`}>
      <Navigation />

      {/* Simple Hero Section */}
      <div className="bg-gradient-to-br from-primary-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-primary-100/50 backdrop-blur-sm rounded-full">
            <span className="text-primary-600 text-sm font-medium">
              Self-Guided Learning
            </span>
          </div>
          <h1 className="text-4xl font-light text-gray-900 mb-4">
            Wellness Resources & Guides
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Thoughtfully curated resources to support your healing journey at
            your own pace. Each guide is crafted with love and backed by
            evidence-based practices.
          </p>
        </div>
      </div>

      {/* Content */}
      <div
        className={`max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 ${theme.spacing.section}`}
      >
        {error && (
          <div
            className={`bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-700 px-6 py-4 ${theme.radius.large} mb-8 ${theme.shadows.small}`}
          >
            {error}
            <button
              onClick={() => setError(null)}
              className={`ml-3 text-red-500 hover:text-red-700 transition-colors ${theme.animations.duration}`}
            >
              ✕
            </button>
          </div>
        )}

        {/* Premium Resources Section */}
        <section id="premium-content" className="py-20">
          <div>
            <div className="text-center mb-12"></div>

            {/* Search and Filter Bar */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Search Input */}
                  <div className="md:col-span-2">
                    <div className="relative">
                      <svg
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by keyword, health concern, or topic..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Price Filter */}
                  <div>
                    <select
                      value={priceFilter}
                      onChange={(e) => setPriceFilter(e.target.value as any)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                    >
                      <option value="all">All Prices</option>
                      <option value="under50">Under $50</option>
                      <option value="50to100">$50 - $100</option>
                      <option value="over100">Over $100</option>
                    </select>
                  </div>
                </div>

                {/* Saved Items Indicator */}
                {savedItems.length > 0 && (
                  <div className="mt-4 text-sm text-primary-600">
                    <span className="inline-flex items-center gap-2">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                      </svg>
                      {savedItems.length} items saved
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Pills */}
          {contentTypes.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setActiveFilter("all")}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  activeFilter === "all"
                    ? "bg-primary-500 text-white shadow-lg"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                All Resources
              </button>
              {contentTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveFilter(type)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    activeFilter === type
                      ? "bg-primary-500 text-white shadow-lg"
                      : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
                >
                  {getContentIcon(type)}{" "}
                  {type.charAt(0).toUpperCase() +
                    type.slice(1).replace("_", " ")}
                </button>
              ))}
            </div>
          )}

          {filteredItems.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-16 text-center border border-gray-200">
              <div className="text-gray-400 text-7xl mb-6">🌱</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {activeFilter === "all"
                  ? "Wellness Resources Coming Soon"
                  : "No resources in this category yet"}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                We're carefully curating healing resources just for you. Each
                piece of content is thoughtfully designed to support your unique
                wellness journey.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  data-testid="content-item"
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
                          <span className="text-2xl">
                            {getContentIcon(item.content_type)}
                          </span>
                        </div>
                        <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                          {item.content_type.replace("_", " ").toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600 transition-colors flex-1">
                        {item.title}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSaveItem(item.id);
                        }}
                        className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label={
                          savedItems.includes(item.id)
                            ? "Remove from saved"
                            : "Save for later"
                        }
                      >
                        <svg
                          className={`w-6 h-6 ${savedItems.includes(item.id) ? "text-red-500 fill-current" : ""}`}
                          fill={
                            savedItems.includes(item.id)
                              ? "currentColor"
                              : "none"
                          }
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                          />
                        </svg>
                      </button>
                    </div>

                    <p className="text-gray-600 text-sm mb-6 line-clamp-4 leading-relaxed">
                      {item.description}
                    </p>

                    {item.tags && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {JSON.parse(item.tags).map(
                          (tag: string, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          )
                        )}
                      </div>
                    )}

                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-semibold text-gray-900">
                          {formatPrice(item.price_cents)}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                          Instant Download
                        </span>
                      </div>

                      {showPurchaseForm === item.id ? (
                        <div className="space-y-3">
                          <input
                            type="email"
                            placeholder="Your email address"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                            required
                          />
                          <input
                            type="text"
                            placeholder="Your full name (optional)"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
                          />
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleContentPurchase(item)}
                              disabled={
                                purchasing === item.id || !customerEmail
                              }
                              className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-3 rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {purchasing === item.id
                                ? "Processing..."
                                : "Complete Purchase"}
                            </button>
                            <button
                              onClick={() => setShowPurchaseForm(null)}
                              className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowPurchaseForm(item.id)}
                          className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 transition-all"
                        >
                          Get Instant Access
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};
